import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { TensionLightingSection } from "@/components/TensionLightingSection";
import hero from "@/assets/playground-shade.jpg";
import heroPicture from "@/assets/playground-shade.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import ropePads from "@/assets/playground-rope-climber-padded-anchors-serge-ferrari.jpg";
import sailUnder from "@/assets/sail-shade-blue-underside.jpg";
import sailBall from "@/assets/sail-shade-ballfield-blue-canopies.jpg";
import sailAfter from "@/assets/sail-shade-after-black.jpg";
import phifertex from "@/assets/sail-shade-phifertex-fabric.jpg";

export const Route = createFileRoute("/playground-shade")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Playground Shade Sails & Equipment Pads — Texas | Texas Made Shade",
      description:
        "Custom playground shade sails and Serge Ferrari® equipment pads for Texas schools, parks, daycares and HOAs. Cuts surface temps up to 30°F, blocks 95% UV.",
      image: hero,
      imageAlt: "Custom shade sail canopy installed over a children's playground in Texas",
      socialTitle: "Playground Shade Sails — Texas Made Shade",
      socialDescription:
        "Engineered playground shade sails and rope-climber pads for Texas schools, parks and daycares.",
      keywords:
        "playground shade sail Texas, playground shade structure, daycare playground shade, school playground sail, park playground shade, HOA playground shade, rope climber padding, playground equipment pads",
    }),
    scripts: serviceHeadScripts({
      path: "/playground-shade",
      serviceName: "Playground Shade",
      breadcrumbName: "Playground Shade",
      description:
        "Custom playground shade sails and equipment padding for Texas schools, daycares, parks and HOA communities — fabricated in Seguin and engineered for Texas.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/playground-shade"
      serviceName="Playground Shade"
      breadcrumbs={[{ label: "Playground Shade" }]}
      whyUsPlacement="afterHero"
      eyebrow="★ PLAYGROUND SHADE ★"
      title="Playground Shade Sails & Equipment Pads — Texas"
      intro="Custom shade sails over playground equipment cut surface temps by up to 30°F and block 95% UV — protecting kids from burns and extending the life of plastic and rubber play surfaces. We design, engineer, fabricate and install playground shade for schools, daycares, parks and HOAs across Texas."
      heroImage={heroPicture}
      features={[
        "Tensioned shade sails sized to standard play structures",
        "Cuts surface temps up to 30°F, blocks 95% UV",
        "Engineered welded steel posts — Texas wind-rated",
        "Serge Ferrari® and Phifertex® UV-stable shade fabric",
        "Custom Serge Ferrari® hardware and anchor padding",
        "School- and HOA-color matching available",
      ]}
      body={[
        { heading: "Playground Shade Sails", text: "Tensioned shade sails over swings, slides and climber structures — sized to your existing equipment and rated for Texas wind. Posts set in engineered concrete footings, stainless cable hardware and quick-release options for storm prep." },
        { heading: "Why Playground Shade Matters", text: "Uncovered playground surfaces in Texas summer routinely measure 140°F+ — hot enough to burn skin in seconds. A properly tensioned UV sail drops surface temp by 25–30°F and blocks 95% of UV, which is the difference between a usable playground in July and a closed one." },
        { heading: "Rope-Climber & Anchor Padding", text: "Custom-sewn Serge Ferrari® padded covers for rope-climber anchors, web-structure hardware and impact zones. Wipe-clean coated fabric in school colors that holds up to daily use, mulch and Texas weather.", image: { src: ropePads, alt: "Custom teal Serge Ferrari padded anchor covers fabricated by Texas Made Shade and installed on a playground rope-climber" } },
        { heading: "Schools, Parks & Daycares", text: "We work with ISDs, charter schools, daycare operators, HOA boards and city park departments across Texas. PO-ready quotes, engineered designs, scheduled installs that work around your operating hours.", image: { src: sailBall, alt: "Tensioned sail-shade canopies over a Texas athletic and play area" } },
        { heading: "Hardware Built for Texas Weather", text: "Stainless tension hardware, welded steel posts powder-coated for UV durability — the same hardware used in our commercial sail-shade work.", image: { src: phifertex, alt: "Phifertex coated-mesh shade-sail fabric" } },
      ]}
      applicationsEyebrow="WHERE WE INSTALL"
      applicationsHeading="Playground Shade Applications"
      applications={[
        "Public Schools & ISDs",
        "Charter & Private Schools",
        "Daycare Centers",
        "City & County Parks",
        "HOA & Neighborhood Parks",
        "Apartment Complex Play Areas",
        "Church Children's Ministries",
        "Splash Pads",
        "Picnic & Pavilion Areas",
        "Outdoor Classroom Decks",
      ]}
      faqs={[
        { q: "How much does a playground shade sail cost in Texas?", a: "Most playground sail-shade projects in Texas run $8,000–$35,000 installed depending on size, post engineering and fabric. Send equipment dimensions and a site photo for a free written quote." },
        { q: "Are your playground shade sails engineered for Texas wind?", a: "Yes — every post and bracket assembly is engineered to the local wind load of the install site, with welded steel posts and stainless cable hardware. Quick-release options are available for storm prep." },
        { q: "How much does shade reduce playground surface temperature?", a: "Independent testing on coated UV mesh shows a 25–30°F drop in surface temperature on rubber and plastic play surfaces under shade — the difference between a 140°F slide and a usable one in July." },
        { q: "Can we match our school or HOA colors?", a: "Yes — Serge Ferrari® and Phifertex® shade fabrics come in a wide range of stock colors, including most common school and brand palettes." },
      ]}
      gallerySection={{
        eyebrow: "Playground Work",
        title: "Recent Playground Shade Installs",
        images: [
          { src: hero, alt: "Custom playground shade sail canopy in Texas" },
          { src: ropePads, alt: "Custom Serge Ferrari padded anchor covers on playground rope-climber" },
          { src: sailUnder, alt: "Underside of tensioned blue shade sail" },
          { src: sailBall, alt: "Tensioned sail-shade canopies over a Texas athletic and play area" },
          { src: sailAfter, alt: "Tensioned black shade sail installed at a Texas property" },
          { src: phifertex, alt: "Phifertex coated-mesh shade-sail fabric" },
        ],
      }}
    >
    <TensionLightingSection />
    </ServicePage>
  ),
});
